import { getModifiedStats } from '@helpers/stats';
import { useTranslation } from 'react-i18next';
import * as Shared from '@shared/lib';
import { useAppStore } from '@stores';

interface AbilityStatsProps {
  ability: Shared.AbilityData;
  chassis: string;
}

interface ChassisAbilityProps {
  chassis: string;
}

// Internal

export function ChassisAbilities({ chassis }: ChassisAbilityProps) {
  const { t: tAbilities } = useTranslation(undefined, { keyPrefix: 'Abilities' });

  const data = Shared.chassisData.find((c) => c.key === chassis);
  if (!data) return null;

  const abilities = data.abilities
    .map((a) => Shared.abilityData.find((ad) => ad.key === a))
    .filter((a) => !!a);
  if (abilities.length === 0) return null;

  return (
    <div className="border-t border-secondary mt-1">
      {abilities.map((a) => (
        <div className="border-b border-secondary flex bg-black/40" key={a.ascentian}>
          <div className="w-1/5 flex-shrink-0 flex items-center justify-center border-r border-secondary">
            <p className="text-2xl text-ascentian active text-primary">
              {a.ascentian}
            </p>
          </div>

          <div className="flex flex-col justify-between w-full">
            <div className="p-2">
              <p className="text-gray-100 leading-tight">{tAbilities(`${a.key}.name`)}</p>
              <p className="text-gray-300 text-xs my-2">{tAbilities(`${a.key}.description`)}</p>
            </div>

            <AbilityStats chassis={chassis} ability={a} />
          </div>
        </div>
      ))}
    </div>
  );
}

function AbilityStats({ ability, chassis }: AbilityStatsProps) {
  const { t } = useTranslation(undefined, { keyPrefix: 'ChassisInfo.Abilities' });
  const { userData } = useAppStore();

  if (!ability.localeData) return null;

  const userChassis = userData.chassis.find((c) => c.key === chassis);
  if (!userChassis) return null;

  const stats: Shared.PlayerStats = getModifiedStats(userData, userChassis.id, 'chassis');

  const mappedStats = Object.entries(ability.localeData).map(([key, value]) => {
    if (key === 'type') return null;

    const format = (val: string | number) => {
      if (typeof val !== 'number') return val;
      if (['damage', 'heal', 'fire'].includes(key)) return Math.round(val);
      if (key.endsWith('Duration')) return `${(val / 1000).toFixed(1)}s`;

      return `${(val * 100).toFixed(2)}%`;
    };

    const compareStat = () => {
      if (typeof value !== 'number') return null;

      if (key.endsWith('Duration')) {
        if (stats.abilityDuration === 1) return null;
        return format(value * stats.abilityDuration);
      }

      if (stats.abilityStrength === 1) return null;
      return format(value * stats.abilityStrength);
    };

    return (
      <div className="px-2 text-xs flex justify-between py-[3px] even:bg-white/10" key={key}>
        <p className="text-gold">{t(`Types.${key}`)}</p>
        <div className="flex items-center">
          {compareStat() && (
            <p className="active mr-2 text-mono">{compareStat()}</p>
          )}
          <p>{format(value)}</p>
        </div>
      </div>
    );
  });

  const energy = Math.round((ability.energy ?? 0) / stats.abilityEfficiency);
  const cooldown = Math.round((ability.cooldown ?? 0) * stats.abilityCooldown);

  return (
    <div className="border-t border-secondary w-full pt-1">
      <p className="px-2 text-xs text-red-400 mb-1">
        {t('targetType')}: {t(`TargetTypes.${ability.localeData.type}`)}
      </p>

      <div className="px-2 text-xs flex justify-between py-[3px] even:bg-white/10">
        <p className="text-gold">{t('energy')}</p>
        <div className="flex items-center">
          {energy !== ability.energy && (
            <p className="active mr-2 text-mono">{energy}</p>
          )}
          <p>{ability.energy ?? 0}</p>
        </div>
      </div>

      <div className="px-2 text-xs flex justify-between py-[3px] even:bg-white/10">
        <p className="text-gold">{t('cooldown')}</p>
        <div className="flex items-center">
          {cooldown !== ability.cooldown && (
            <p className="active mr-2 text-mono">{cooldown}s</p>
          )}
          <p>{ability.cooldown ?? 0}s</p>
        </div>
      </div>

      {mappedStats}
    </div>
  );
}